import { onMounted, onUnmounted } from 'vue'

export interface Shortcut {
  // For regular shortcuts: set key + optional ctrl/shift/alt/meta
  key?: string
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  meta?: boolean
  // For two-key sequences like 'g d': set seq to ['g', 'd']
  seq?: [string, string]
  handler: () => void
  // Skip firing when focus is inside Monaco editor or an input
  skipInInput?: boolean
}

export function useShortcuts(shortcuts: Shortcut[]) {
  let pendingSeqKey = ''
  let pendingSeqTime = 0

  function isInInput(target: EventTarget | null): boolean {
    if (!target) return false
    const el = target as HTMLElement
    const tag = el.tagName?.toLowerCase()
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
    if (el.isContentEditable) return true
    if (el.closest?.('.monaco-editor')) return true
    return false
  }

  function handleKeydown(e: KeyboardEvent) {
    const now = Date.now()
    const key = e.key.toLowerCase()

    for (const s of shortcuts) {
      if (s.skipInInput !== false && isInInput(e.target)) continue

      // Two-key sequence (e.g. 'g d')
      if (s.seq) {
        const [first, second] = s.seq
        if (key === second && pendingSeqKey === first && now - pendingSeqTime < 1000 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault()
          pendingSeqKey = ''
          s.handler()
          return
        }
        continue
      }

      // Regular shortcut
      if (!s.key) continue
      const needCtrl  = s.ctrl  ?? false
      const needShift = s.shift ?? false
      const needAlt   = s.alt   ?? false
      const ctrlMatch  = needCtrl  ? (e.ctrlKey || e.metaKey) : (!e.ctrlKey && !e.metaKey)
      const shiftMatch = needShift ? e.shiftKey : !e.shiftKey
      const altMatch   = needAlt   ? e.altKey   : !e.altKey
      const keyMatch   = key === s.key.toLowerCase()

      if (ctrlMatch && shiftMatch && altMatch && keyMatch) {
        e.preventDefault()
        s.handler()
        return
      }
    }

    // Track first key of any potential sequence
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && key.length === 1) {
      pendingSeqKey  = key
      pendingSeqTime = now
    } else {
      pendingSeqKey = ''
    }
  }

  onMounted(()   => window.addEventListener('keydown', handleKeydown))
  onUnmounted(() => window.removeEventListener('keydown', handleKeydown))
}
